import { centsFromYuan, formatYuan } from '@cuito/ui';

export interface SchemeFormItem {
  productId: string;
  quantity: number;
  /** 单品原价（分），由商品列表带出 */
  priceCents: number;
}

export interface SchemeFormInput {
  items: SchemeFormItem[];
  priceCents: number;
}

export interface SchemeFormResult {
  ok: boolean;
  errors: { items?: string; priceCents?: string };
  originalTotalCents: number;
  discountCents: number;
  perItemDiscountCents: number;
  itemCount: number;
  payableTotalCents: number;
}

/**
 * 促销方案表单的即时校验与金额预览。
 *
 * **与后端 `allocateDiscount`（`apps/api/src/modules/promotions/allocation.ts`）保持同一口径**：
 * 每件优惠 `floor((P − Q) / N)`，向下取整。前端把「省多少钱」提前算给用户看，
 * 但**真正的校验与计算以后端为准**——前端只负责让用户在提交前就知道会失败。
 *
 * 文案逐字取自 `05-ui-design-system.md` §3.2，与后端错误码（`SCHEME_ITEMS_EMPTY` /
 * `SCHEME_DUPLICATE_PRODUCT` / `SCHEME_NO_DISCOUNT`）一一对应。
 */
export function validateSchemeForm(input: SchemeFormInput): SchemeFormResult {
  const errors: SchemeFormResult['errors'] = {};

  const itemCount = input.items.reduce((sum, i) => sum + i.quantity, 0);
  const originalTotalCents = input.items.reduce(
    (sum, i) => sum + i.priceCents * i.quantity,
    0,
  );

  if (input.items.length === 0) {
    errors.items = '请至少选择一个商品';
  } else if (input.items.some((i) => i.quantity <= 0)) {
    errors.items = '件数必须大于 0';
  } else {
    const ids = input.items.map((i) => i.productId);
    if (new Set(ids).size !== ids.length) {
      errors.items = '同一商品不能在一个方案里出现两次';
    }
  }

  if (!errors.items && input.priceCents >= originalTotalCents) {
    errors.priceCents = '方案价必须低于原价合计';
  }

  const perItemDiscountCents =
    itemCount > 0 ? Math.max(0, Math.floor((originalTotalCents - input.priceCents) / itemCount)) : 0;

  return {
    ok: Object.keys(errors).length === 0,
    errors,
    originalTotalCents,
    discountCents: Math.max(0, originalTotalCents - input.priceCents),
    perItemDiscountCents,
    itemCount,
    payableTotalCents: originalTotalCents - perItemDiscountCents * itemCount,
  };
}

/**
 * 分 → 「¥1,280.00」展示串。**实现只有一份**：`@cuito/ui` 的 `formatYuan`（Phase 5 Task 22 收口）。
 *
 * 历史：本函数原先用运行时的本地化数字格式（`Intl` 的货币格式化）。改掉的理由不是偏好 ——
 * `apps/shop` 侧的同一件事（`yuanFromCents`）刻意**不用**运行时的本地化格式，
 * 因为 ICU 数据会让**规范硬性规定**的金额格式随运行时漂移（Node 小镜像 / 不同浏览器版本）。
 * 两处并存就等于「同一个界面元素在不同端的格式由运行环境决定」。
 * 负数位置不变（`-¥1,280.00`），`schemeForm.spec.ts` 的 5 条断言逐条仍然成立，
 * 另加一条**函数引用相等**（`formatCents === formatYuan`）钉住转发关系。
 */
export const formatCents = formatYuan;

/**
 * 服务端金额读不出来时的占位文案（`05-ui-design-system.md` §7.8 + 附录 B1s）。
 *
 * **绝不显示 `¥0.00`**：`0.00` 是「这单真的不要钱」，读不出来是「不知道多少钱」——
 * 把后者显示成前者会让顾客以为免费（Task 18 / Task 19 各抓到过一次同类问题）。
 */
export const AMOUNT_UNAVAILABLE_TEXT = '金额以服务端为准';

/** 同上，用于单价/原价的位置（Task 18 定下的原文） */
export const PRICE_UNAVAILABLE_TEXT = '价格以服务端为准';

/**
 * 服务端的**字符串十进制元** → 展示串；**读不出来返回 `null`**（不是 `¥0.00`）。
 *
 * 展示层的唯一入口（§7.8 的形态由 `formatYuan` 保证）：
 * `centsFromYuan` 对空串 / 坏串 / 契约外形态返回 `null`，调用方据此渲染
 * `AMOUNT_UNAVAILABLE_TEXT` 之类的占位文案。收银端曾有 15 处
 * `formatCents(yuanToCents(x) ?? 0)` —— 那是把「读不出来」说成「零元」。
 *
 * 不用 `yuanToCents`：那是**表单输入**的校验器（比这里更严，拒绝 `'60'` 这种没有
 * 小数点的形态），它的 `null` 是「格式不合法」而不是「展示不出来」。两者不是同一件事。
 */
export function formatServerYuan(price: string | number): string | null {
  const cents = centsFromYuan(price);
  return cents === null ? null : formatCents(cents);
}

/**
 * 「元」输入串 → 分；非法输入返回 `null`。
 *
 * **按小数点拆字符串而不是 `Number(text) * 100`**：后者在 `19.99` 上先得到
 * `1998.9999…` 的浮点中间值，一旦哪次漏掉 `Math.round` 就会把 1 分钱算丢。
 * 金额全程走整数分，不从浮点过手（`04-api-contract.md` §1.7）。
 */
export function yuanToCents(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [yuan = '', fraction = ''] = trimmed.split('.');
  const cents = Number(yuan) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}
